import Phaser from 'phaser';

/**
 * The floating Heaven finale set-piece on the World Map — a radiant gate hanging
 * in the sky ABOVE the painted mortal atlas, the destination of the last stop.
 *
 * It's procedural so it works before any art lands (swap in a `world_heaven`
 * plate later the same way backdrops resolve). Everything is anchored in world
 * space above the crown node, grouped in one container so the scene can fade it
 * in, frame it with the camera, and run the ascension cinematic. `tick()` drives
 * the living motion (drifting clouds, sweeping rays, a gentle bob + bloom).
 */
export class HeavenSetpiece {
  /** The whole set-piece — fade/scale this as one unit. */
  readonly layer: Phaser.GameObjects.Container;
  /** World-space centre the camera should frame during the ascension. */
  readonly focusX: number;
  readonly focusY: number;

  private rays: Phaser.GameObjects.Container;
  private gate: Phaser.GameObjects.Container;
  private halo: Phaser.GameObjects.Image;
  private clouds: { obj: Phaser.GameObjects.Ellipse; x0: number; amp: number; spd: number; ph: number }[] = [];

  constructor(scene: Phaser.Scene, anchorX: number, anchorY: number) {
    // The gate floats above the crown node; clouds settle at the map's top edge.
    const gx = anchorX;
    const gy = anchorY - 360;
    this.focusX = gx;
    this.focusY = gy + 40;

    const objs: Phaser.GameObjects.GameObject[] = [];

    // Soft radiant sky-glow behind everything (reuse the fog brush as a radial).
    const glow = scene.add.image(gx, gy, 'fog_brush').setDisplaySize(2200, 2000).setTint(0xfff0c8).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.55);
    objs.push(glow);

    // God-rays radiating from the gate — thin gold wedges, additive, slow spin.
    this.rays = scene.add.container(gx, gy);
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      const len = 1500;
      const wedge = scene.add.triangle(0, 0, 0, -26, len * Math.cos(a), len * Math.sin(a) - 26, len * Math.cos(a + 0.05), len * Math.sin(a + 0.05) + 26, 0xffe9a8, 0.1).setBlendMode(Phaser.BlendModes.ADD);
      this.rays.add(wedge);
    }
    objs.push(this.rays);

    // A bright bloom disc right behind the gate threshold.
    this.halo = scene.add.image(gx, gy + 10, 'fog_brush').setDisplaySize(720, 720).setTint(0xfffbe6).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.7);
    objs.push(this.halo);

    // The gate itself.
    this.gate = this.makeGate(scene, gx, gy);
    objs.push(this.gate);

    // Cloud bank — a soft floor of overlapping ellipses hiding the seam where
    // Heaven meets the top of the map, plus a few drifting cloudlets up high.
    const cloudDefs: [number, number, number, number, number][] = [
      // x, y, w, h, alpha
      [gx - 360, anchorY + 30, 520, 150, 0.9],
      [gx - 40, anchorY + 70, 620, 170, 0.95],
      [gx + 330, anchorY + 35, 520, 150, 0.9],
      [gx - 560, anchorY - 10, 360, 120, 0.8],
      [gx + 560, anchorY - 5, 380, 120, 0.8],
      [gx - 240, gy - 230, 240, 70, 0.5],
      [gx + 300, gy - 140, 280, 80, 0.5],
      [gx + 60, gy - 320, 200, 60, 0.45],
    ];
    for (const [x, y, w, h, al] of cloudDefs) {
      const c = scene.add.ellipse(x, y, w, h, 0xfdfbf4, al);
      objs.push(c);
      this.clouds.push({ obj: c, x0: x, amp: 18 + Math.random() * 16, spd: 0.0003 + Math.random() * 0.0004, ph: Math.random() * Math.PI * 2 });
    }

    this.layer = scene.add.container(0, 0, objs).setDepth(11);
  }

  /** Two luminous columns + an arch + a glowing threshold — a heavenly gate. */
  private makeGate(scene: Phaser.Scene, gx: number, gy: number): Phaser.GameObjects.Container {
    const g = scene.add.graphics();
    const top = gy - 250;
    const bot = gy + 330;
    const halfSpan = 150;
    const colW = 42;

    // Threshold light between the columns (bright, faded at edges).
    g.fillStyle(0xfff7dc, 0.5).fillRoundedRect(gx - halfSpan + colW, top + 60, (halfSpan - colW) * 2, bot - top - 60, 30);

    for (const sx of [-1, 1]) {
      const cx = gx + sx * halfSpan;
      // Column body with a lit inner edge for a hint of round form.
      g.fillStyle(0xcfa24a, 1).fillRoundedRect(cx - colW / 2, top, colW, bot - top, 10);
      g.fillStyle(0xf4dd9a, 1).fillRect(cx - colW / 2 + 5, top + 6, 9, bot - top - 12);
      g.fillStyle(0x8a6a28, 0.8).fillRect(cx + colW / 2 - 9, top + 6, 5, bot - top - 12);
      // Capital + base.
      g.fillStyle(0xe9cf86, 1).fillRoundedRect(cx - colW / 2 - 9, top - 14, colW + 18, 18, 5);
      g.fillStyle(0xe9cf86, 1).fillRoundedRect(cx - colW / 2 - 9, bot - 6, colW + 18, 18, 5);
    }

    // The arch joining the capitals.
    g.lineStyle(26, 0xe1c178, 1);
    g.beginPath();
    g.arc(gx, top - 6, halfSpan, Math.PI, Math.PI * 2);
    g.strokePath();
    g.lineStyle(8, 0xf6e3a6, 1);
    g.beginPath();
    g.arc(gx, top - 6, halfSpan, Math.PI, Math.PI * 2);
    g.strokePath();
    // Keystone glow.
    g.fillStyle(0xfff7dc, 0.9).fillCircle(gx, top - 6 - halfSpan, 14);

    return scene.add.container(0, 0, [g]);
  }

  /** Living motion — call each frame with the scene clock (ms). */
  tick(timeMs: number): void {
    this.rays.rotation = timeMs * 0.00004;
    const pulse = 0.6 + 0.16 * Math.sin(timeMs / 520);
    this.halo.setAlpha(pulse);
    // Gentle bob of the gate so it reads as floating.
    this.gate.y = Math.sin(timeMs / 900) * 6;
    for (const c of this.clouds) c.obj.x = c.x0 + Math.sin(timeMs * c.spd + c.ph) * c.amp;
  }
}
